// astriaUKV2Theme.ts
import { useEffect, useState } from "react";

// ─── Astria UK V2 — light/dark palette, per Uk.txt's "UK UI JSON" spec:
// background_light #F5F5F5, background_dark #1C1C1C, text_light #2E2E2E,
// text_dark #EAEAEA, accent #4C4C4C, highlight #A3A3A3 (accent/highlight
// stay the same in both modes — only background/text/surface shift).
//
// Scoped to the UK lane only (persisted under its own localStorage key) —
// no other Astria lane in this app has a dark mode, so this doesn't touch
// any shared/global theme mechanism.

export const UK_V2_HEADING_FONT = "'Georgia', 'Times New Roman', serif";
export const UK_V2_BODY_FONT = "'Inter', sans-serif";

export interface UKV2Palette {
  bg: string;
  card: string;
  section: string;
  cardBorder: string;
  titleColor: string;
  sectionTitleColor: string;
  bodyColor: string;
  mutedColor: string;
  accent: string;
  accentSoft: string;
  accentGlow: string;
  highlight: string;
  highlightSoft: string;
  white: string;
  headerBg: string;
}

const LIGHT_PALETTE: UKV2Palette = {
  bg: "#F5F5F5",
  card: "#FFFFFF",
  section: "#EFEFEF",
  cardBorder: "#DCDCDC",
  titleColor: "#2E2E2E",
  sectionTitleColor: "#2E2E2E",
  bodyColor: "#3D3D3D",
  mutedColor: "#8A8A8A",
  accent: "#4C4C4C",
  accentSoft: "rgba(76,76,76,0.08)",
  accentGlow: "0 0 0 3px rgba(76,76,76,0.14)",
  highlight: "#A3A3A3",
  highlightSoft: "rgba(163,163,163,0.16)",
  white: "#FFFFFF",
  headerBg: "rgba(245,245,245,0.85)",
};

const DARK_PALETTE: UKV2Palette = {
  bg: "#1C1C1C",
  card: "#262626",
  section: "#2E2E2E",
  cardBorder: "#3D3D3D",
  titleColor: "#EAEAEA",
  sectionTitleColor: "#EAEAEA",
  bodyColor: "#D6D6D6",
  mutedColor: "#9C9C9C",
  accent: "#A3A3A3",
  accentSoft: "rgba(163,163,163,0.14)",
  accentGlow: "0 0 0 3px rgba(163,163,163,0.2)",
  highlight: "#4C4C4C",
  highlightSoft: "rgba(76,76,76,0.28)",
  white: "#FFFFFF",
  headerBg: "rgba(28,28,28,0.85)",
};

// Kept as a plain export (light palette) for any call site that doesn't need
// to react to the toggle — e.g. static color references outside a component.
export const SHELL_COLORS_UK_V2 = LIGHT_PALETTE;

// ─── Element colors — per Uk.txt's "ui_personality.element_colors" spec.
// Purely additive: an optional accent used only where a zodiac sign's
// element is displayed (e.g. the sign symbol on ZodiacHomeScreen). Doesn't
// replace or touch the light/dark palette above.
export type ZodiacElement = "fire" | "earth" | "air" | "water";

export const UK_V2_ELEMENT_COLORS: Record<ZodiacElement, string> = {
  fire: "#E2542D",
  earth: "#2E2E2E",
  air: "#3B77C2",
  water: "#2E8B57",
};

export const ZODIAC_SIGN_ELEMENT: Record<string, ZodiacElement> = {
  aries: "fire",
  leo: "fire",
  sagittarius: "fire",
  taurus: "earth",
  virgo: "earth",
  capricorn: "earth",
  gemini: "air",
  libra: "air",
  aquarius: "air",
  cancer: "water",
  scorpio: "water",
  pisces: "water",
};

const STORAGE_KEY = "astriaUKV2Theme";

function getStoredTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
}

export function useUKV2Theme(): {
  theme: "light" | "dark";
  colors: UKV2Palette;
  toggleTheme: () => void;
} {
  const [theme, setTheme] = useState<"light" | "dark">(getStoredTheme);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return { theme, colors: theme === "dark" ? DARK_PALETTE : LIGHT_PALETTE, toggleTheme };
}
